<template>
  <transition name="booking-bar-slide">
    <div
      v-if="visible"
      class="booking-floating-bar elevation-8"
    >
      <v-btn
        icon
        dark
        small
        class="booking-floating-bar__close"
        @click="dismiss"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <v-container class="pa-0">
        <v-row
          no-gutters
          align="center"
        >
          <!-- Object name -->
          <v-col
            cols="12"
            class="booking-floating-bar__title"
          >
            <v-icon
              color="white"
              class="mr-2 booking-floating-bar__home-icon"
            >
              fa fa-home
            </v-icon>
            <a
              class="booking-floating-bar__object-link"
              @click.prevent="goToObject"
            >
              <strong>{{ pendingBooking.objectName }}</strong>
            </a>
            <span
              v-if="pendingBooking.address"
              class="booking-floating-bar__address hidden-xs-only"
            >
              , {{ pendingBooking.address }}
            </span>
          </v-col>

          <!-- Details row -->
          <v-col
            cols="12"
            sm="8"
            class="booking-floating-bar__details"
          >
            <!-- Dates -->
            <span class="booking-floating-bar__detail">
              <v-icon
                color="rgba(255,255,255,0.8)"
                small
                class="mr-1"
              >
                mdi-calendar-range
              </v-icon>
              {{ pendingBooking.dateStart }} &ndash; {{ pendingBooking.dateEnd }}
              <span
                v-if="pendingBooking.nights"
                class="booking-floating-bar__nights"
              >
                ({{ pendingBooking.nights }} {{ pendingBooking.nights === 1 ? $t('öö') : $t('ööd') }})
              </span>
            </span>

            <!-- Guests -->
            <span
              v-if="guestsText"
              class="booking-floating-bar__detail"
            >
              <v-icon
                color="rgba(255,255,255,0.8)"
                small
                class="mr-1"
              >
                mdi-account-group-outline
              </v-icon>
              {{ guestsText }}
            </span>

            <!-- Reservation fee (amount to pay) -->
            <span
              v-if="pendingBooking.reservationFee"
              class="booking-floating-bar__detail"
            >
              <v-icon
                color="rgba(255,255,255,0.8)"
                small
                class="mr-1"
              >
                mdi-tag-outline
              </v-icon>
              <strong>{{ pendingBooking.reservationFee }} &euro;</strong>
            </span>
          </v-col>

          <!-- Payment button -->
          <v-col
            cols="12"
            sm="4"
            class="booking-floating-bar__actions"
          >
            <v-btn
              color="#ffc800"
              class="booking-floating-bar__btn"
              @click="goToPayment"
            >
              <v-icon
                left
                small
              >
                mdi-credit-card-outline
              </v-icon>
              {{ $t('complete_payment') || 'Complete Payment' }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </transition>
</template>

<script>
import api from '@/store/api'

const EXPIRY_MS = 3 * 60 * 60 * 1000 // 3 hours
const RECHECK_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const INITIAL_CHECK_DELAY_MS = 3000 // 3 seconds after page load

export default {
  name: 'BookingFloatingBar',

  data() {
    return {
      pendingBooking: null,
      dismissed: false,
      verified: false,
      recheckTimer: null,
      validApiResponse: true,
    }
  },

  computed: {
    isPaymentPage() {
      return this.$route.name === 'paymentForm'
    },

    visible() {
      return (
        this.pendingBooking &&
        !this.dismissed &&
        !this.isPaymentPage &&
        this.verified
      )
    },

    guestsText() {
      if (!this.pendingBooking) return ''
      const parts = []
      if (this.pendingBooking.adults) {
        parts.push(this.pendingBooking.adults + ' ' + this.$t('adults_count'))
      }
      if (this.pendingBooking.kids) {
        parts.push(this.pendingBooking.kids + ' ' + this.$t('kids_count'))
      }
      if (this.pendingBooking.infants) {
        parts.push(this.pendingBooking.infants + ' ' + this.$t('infants_count'))
      }
      return parts.join(', ')
    },
  },

  watch: {
    '$route.path'() {
      this.loadPendingBooking()
      // When navigating away from payment page, re-verify
      if (!this.isPaymentPage && this.pendingBooking) {
        this.verifyBookingStatus()
      }
    },
  },

  created() {
    this.loadPendingBooking()
    // Initial server check after a short delay so page loads first
    if (this.pendingBooking) {
      setTimeout(() => {
        this.verifyBookingStatus()
      }, INITIAL_CHECK_DELAY_MS)
    }
    this.startRecheckTimer()
  },

  beforeDestroy() {
    this.stopRecheckTimer()
  },

  methods: {
    loadPendingBooking() {
      try {
        const raw = localStorage.getItem('pending_booking')
        if (!raw) {
          this.pendingBooking = null
          this.verified = false
          return
        }
        const data = JSON.parse(raw)
        if (data.timestamp && Date.now() - data.timestamp > EXPIRY_MS) {
          localStorage.removeItem('pending_booking')
          this.pendingBooking = null
          this.verified = false
          return
        }
        this.pendingBooking = data
        this.dismissed = false
      } catch (e) {
        this.pendingBooking = null
        this.verified = false
      }
    },

    async verifyBookingStatus() {
      if (!this.pendingBooking || !this.pendingBooking.bookingId) return
      if (!this.validApiResponse) return

      try {
        const response = await api.getOneItemWithQuery(
          'booking-to-pay',
          this.pendingBooking.bookingId,
          [{ key: 'token', value: encodeURIComponent(this.pendingBooking.paymentToken || '') }]
        )

        // Validate response is a proper object with reservationFee
        if (
          !response ||
          typeof response !== 'object' ||
          !response.status ||
          response.reservationFee === undefined
        ) {
          // Invalid response — stop polling, clear bar
          this.validApiResponse = false
          this.clearBooking()
          return
        }

        // If paid or no card payments — clear
        if (response.status !== 'ok' || !response.cardPayments) {
          this.clearBooking()
          return
        }

        // Booking is still unpaid — show the bar
        this.verified = true
      } catch (e) {
        // API error — stop future polling but keep bar if already verified
        this.validApiResponse = false
      }
    },

    startRecheckTimer() {
      this.stopRecheckTimer()
      this.recheckTimer = setInterval(() => {
        if (
          this.pendingBooking &&
          this.validApiResponse &&
          !this.isPaymentPage
        ) {
          this.verifyBookingStatus()
        }
      }, RECHECK_INTERVAL_MS)
    },

    stopRecheckTimer() {
      if (this.recheckTimer) {
        clearInterval(this.recheckTimer)
        this.recheckTimer = null
      }
    },

    clearBooking() {
      localStorage.removeItem('pending_booking')
      this.pendingBooking = null
      this.verified = false
      this.stopRecheckTimer()
    },

    goToObject() {
      if (!this.pendingBooking || !this.pendingBooking.objectSeoName) return
      const lang =
        this.pendingBooking.lang || this.$store.state.lang || 'en'
      const path =
        '/' + this.pendingBooking.objectSeoName + '_' + lang + '_object.html'
      this.$router.push({ path }).catch(() => {})
    },

    goToPayment() {
      if (!this.pendingBooking) return
      const lang =
        this.pendingBooking.lang || this.$store.state.lang || 'en'
      const path =
        '/' + lang + '/pay/reservation/' + this.pendingBooking.bookingId + '/?token=' +
        encodeURIComponent(this.pendingBooking.paymentToken || '')
      this.$router.push({ path }).catch(() => {})
    },

    dismiss() {
      this.dismissed = true
    },
  },
}
</script>

<style lang="scss" scoped>
.booking-floating-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1002;
  background: linear-gradient(135deg, #167a9e 0%, #1a8db8 40%, #219bc8 100%);
  color: white;
  padding: 14px 24px;
  border-radius: 12px 12px 0 0;
  backdrop-filter: blur(12px);
  box-shadow:
    0 -4px 20px rgba(0, 0, 0, 0.15),
    0 -1px 6px rgba(0, 0, 0, 0.08);

  &__close {
    position: absolute;
    top: 8px;
    right: 10px;
    opacity: 0.6;
    transition: opacity 0.2s;
    &:hover {
      opacity: 1;
    }
  }

  &__home-icon {
    font-size: 20px !important;
    align-self: flex-start;
    margin-top: 1px;
  }

  &__title {
    display: flex;
    align-items: baseline;
    font-size: 15px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-right: 30px;
    margin-bottom: 8px;
    letter-spacing: 0.2px;
  }

  &__object-link {
    color: white;
    text-decoration: none;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }

  &__address {
    font-weight: normal;
    opacity: 0.8;
    font-size: 13px;
  }

  &__details {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    font-size: 14px;
  }

  &__detail {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.15);
    padding: 3px 10px;
    border-radius: 20px;
    line-height: 1.4;
  }

  &__nights {
    opacity: 0.85;
    margin-left: 3px;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  &__btn {
    color: rgba(0, 0, 0, 0.87) !important;
    font-weight: 700;
    text-transform: none;
    letter-spacing: 0.2px;
    font-size: 14px !important;
    padding: 0 20px !important;
    animation: btn-pulse 2.5s ease-in-out 3;
  }
}

@keyframes btn-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 200, 0, 0.5);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(255, 200, 0, 0);
  }
}

.booking-bar-slide-enter-active,
.booking-bar-slide-leave-active {
  transition: transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.35s ease;
}

.booking-bar-slide-enter,
.booking-bar-slide-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

@media screen and (max-width: 600px) {
  .booking-floating-bar {
    padding: 12px 14px;
    border-radius: 10px 10px 0 0;

    &__home-icon {
      font-size: 15px !important;
      align-self: center;
      margin-top: 0;
    }

    &__title {
      font-size: 14px;
      justify-content: center;
      padding-right: 24px;
    }

    &__details {
      justify-content: center;
      font-size: 13px;
      margin-bottom: 8px;
    }

    &__actions {
      justify-content: center;
    }

    &__btn {
      width: 100%;
    }
  }
}
</style>
